import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import components (lazy-loaded for performance)
const VideoGallery = React.lazy(() => import("./VideoGallery"));
const UploadVideo = React.lazy(() => import("./UploadVideo"));
const AddVideoByCID = React.lazy(() => import("./AddVideoByCID"));
const VideoViewer = React.lazy(() => import("./VideoViewer"));
const IPFSVideosAdmin = React.lazy(() => import("./IPFSVideosAdmin"));
const EditVideoDetails = React.lazy(() => import("./EditVideoDetails"));

const youtubeToIPFSModuleConfig: ModuleConfig = {
    // 🏷️ MODULE IDENTITY
    id: "youtubetoipfs",
    name: "IPFS Videos",
    description: "Upload and manage videos on IPFS decentralized storage",
    version: "1.0.0",
    enabled: true,
    category: "media",
    order: 10,
    icon: "🎬",
    color: "#8B5CF6",

    // 🛣️ ROUTES
    routes: [
        {
            path: "/youtubetoipfs",
            component: VideoGallery,
            permissions: ["IPFS_ADMIN", "IPFS_USER"]
        },
        {
            path: "/youtubetoipfs/upload",
            component: UploadVideo,
            permissions: ["IPFS_ADMIN", "IPFS_USER"]
        },
        {
            path: "/youtubetoipfs/add-by-cid",
            component: AddVideoByCID,
            permissions: ["IPFS_ADMIN", "IPFS_USER"]
        },
        {
            path: "/youtubetoipfs/video/:id/edit",
            component: EditVideoDetails,
            permissions: ["IPFS_ADMIN", "IPFS_USER"]
        },
        {
            path: "/youtubetoipfs/video/:id",
            component: VideoViewer,
            permissions: [] // Public - anyone can view shared videos
        },
        {
            path: "/youtubetoipfs/admin",
            component: IPFSVideosAdmin,
            permissions: ["IPFS_ADMIN"]
        }
    ],

    // 🧭 NAVIGATION
    navigation: [
        {
            label: "Video Gallery",
            path: "/youtubetoipfs",
            icon: "🎬",
            permissions: ["IPFS_ADMIN", "IPFS_USER"]
        },
        {
            label: "Upload Video",
            path: "/youtubetoipfs/upload",
            icon: "⬆️",
            permissions: ["IPFS_ADMIN", "IPFS_USER"]
        },
        {
            label: "Add by CID",
            path: "/youtubetoipfs/add-by-cid",
            icon: "#️⃣",
            permissions: ["IPFS_ADMIN", "IPFS_USER"]
        },
        {
            label: "Admin Dashboard",
            path: "/youtubetoipfs/admin",
            icon: "⚙️",
            permissions: ["IPFS_ADMIN"]
        }
    ],

    // 🔐 PERMISSIONS
    permissions: {
        view: ["IPFS_ADMIN", "IPFS_USER"],
        create: ["IPFS_ADMIN", "IPFS_USER"],
        edit: ["IPFS_ADMIN", "IPFS_USER"],
        delete: ["IPFS_ADMIN", "IPFS_USER"],
        admin: ["IPFS_ADMIN"]
    },

    // ⚡ QUICK ACTIONS
    quickActions: [
        {
            label: "Upload Video",
            path: "/youtubetoipfs/upload",
            icon: "⬆️",
            description: "Upload a video to IPFS"
        },
        {
            label: "Add by CID",
            path: "/youtubetoipfs/add-by-cid",
            icon: "#️⃣",
            description: "Register existing IPFS video by CID"
        },
        {
            label: "View Gallery",
            path: "/youtubetoipfs",
            icon: "🎬",
            description: "Browse your IPFS video collection"
        },
        {
            label: "Admin Dashboard",
            path: "/youtubetoipfs/admin",
            icon: "⚙️",
            description: "Manage all IPFS videos across users"
        }
    ],

    // 📝 CHANGELOG
    changelog: [
        {
            version: "1.0.0",
            releaseDate: "2025-11-22",
            title: "Stable Release - Production Ready",
            features: [
                "✅ Production-ready IPFS video management",
                "✅ Decentralized video storage and streaming",
                "✅ CID-based video registration",
                "✅ Public video sharing with embed support",
                "✅ Admin dashboard for cross-user management",
                "✅ Video metadata editing and organization"
            ],
            notes: "Official stable release with comprehensive IPFS video management."
        },
        {
            version: "0.5.0",
            releaseDate: "2025-09-10",
            title: "Video Editing & Metadata Management",
            features: [
                "✅ Created EditVideoDetails component (412 lines)",
                "✨ Enhanced video metadata editing",
                "✨ Title and description updates",
                "✨ Privacy settings management",
                "✨ Thumbnail customization"
            ],
            notes: "Video editing capabilities for better content management."
        },
        {
            version: "0.4.0",
            releaseDate: "2025-06-15",
            title: "Admin Dashboard & Cross-User Management",
            features: [
                "✅ Created IPFSVideosAdmin component (523 lines)",
                "✨ Cross-user video management",
                "✨ Bulk operations support",
                "✨ Advanced filtering and search",
                "✨ Usage statistics and analytics"
            ],
            notes: "Admin dashboard for enterprise video management."
        },
        {
            version: "0.3.0",
            releaseDate: "2025-03-08",
            title: "CID Support & External Video Registration",
            features: [
                "✅ Created AddVideoByCID component (301 lines)",
                "✨ Register existing IPFS videos by CID",
                "✨ External video integration",
                "✨ CID validation and verification",
                "✨ Metadata extraction from IPFS"
            ],
            notes: "Support for registering existing IPFS videos without re-uploading."
        },
        {
            version: "0.2.0",
            releaseDate: "2024-12-20",
            title: "Enhanced Gallery & Video Player",
            features: [
                "✨ Enhanced VideoGallery with grid layouts (287 insertions)",
                "✨ Improved VideoViewer with controls (219 lines)",
                "✨ Better video thumbnails and previews",
                "✨ Enhanced search and filtering",
                "✨ Mobile-responsive video player"
            ],
            notes: "Gallery and player improvements for better user experience."
        },
        {
            version: "0.1.0",
            releaseDate: "2024-09-15",
            title: "Initial IPFS Video System",
            features: [
                "✅ Created VideoGallery component (645 lines)",
                "✅ Created UploadVideo component (525 lines)",
                "✅ Created VideoViewer component (579 lines)",
                "✅ Module configuration and routing (72 lines)",
                "✅ IPFS video upload and storage",
                "✅ Public video sharing",
                "✅ Video gallery with search",
                "✅ Role-based permissions (IPFS_ADMIN, IPFS_USER)"
            ],
            notes: "Initial implementation with 1,821 lines of IPFS video management code."
        }
    ],

    // 🗺️ ROADMAP
    roadmap: []

};

export default youtubeToIPFSModuleConfig;